import { getLocalItem, setLocalItem } from "@/lib/commonService";
import { getVistorId } from "../models/customerShow";
import { postflow, getFlowValue } from "../models/products";
import { flowAfter } from "../models/loginModel";

// URL参数接口
interface UrlParams {
    flow?: string;
    utm_source?: string;
    [key: string]: string | undefined;
}

// 流量事件参数接口
interface FlowEventParam {
    eventType: 1 | 2 | 3 | 4 | 5;
    paramValue: string;
    touristId: string;
    productsId?: string | number;
    customersId?: string | number;
    ordersOid?: string;
    price?: string | number;
}

// 订单成功响应接口
interface CheckSuccessResponse {
    extra: {
        orders_oid: string;
        order_total_out_shipping: string | number;
    };
}

/**
 * 解析URL参数
 * @param url - 需要解析的URL字符串
 * @returns 解析后的参数对象
 */
const getUrlParams2 = (url: string): UrlParams => {
    const pattern = /(\w+|[\u4e00-\u9fa5]+)=(\w+|[\u4e00-\u9fa5]+)/gi;
    const result: UrlParams = {};
    url.replace(pattern, (_, key: string, value: string): string => {
        result[key] = value;
        return "";
    });
    return result;
};

/**
 * 流量追踪类
 * 用于处理流量归因、页面浏览、商品点击、用户注册、订单结算等事件追踪
 */
class Flow {
    data: string = ""; // 唯一编码

    /**
     * 初始化流量追踪
     * 处理URL参数中的flow、utm_source等流量信息
     */
    async init(): Promise<void> {
        const _url = window.location.href.replace(/[\[\]]/g, "");
        const urlParams = getUrlParams2(_url);
        const url_flow = urlParams.flow;
        const cache_flow = getLocalItem("flow") as string;
        this.data = cache_flow;

        // URL上有flow参数
        if (url_flow) {
            this.data = url_flow;
            this.pageView();
            return;
        }

        // URL上没有flow，但是有utm_source参数
        if (urlParams.utm_source && !url_flow) {
            try {
                const serviceCreateFlowReq = await getFlowValue({
                    url: window.location.href,
                });
                this.data = serviceCreateFlowReq || cache_flow;
                this.pageView();
                return;
            } catch (error) {
                console.error("获取流量值失败:", error);
                this.data = cache_flow;
            }
        }

        // 子链接功能下线
        // const postflowReq = await postflow({ url: _url });
        // this.data = postflowReq.data || cache_flow;
        this.pageView();
    }

    /**
     * 页面浏览事件
     * 合并了原有的first和middle方法逻辑
     */
    pageView(): void {
        if (!this.data) {
            return;
        }

        const is_first_view = !getLocalItem("flow");

        const param: FlowEventParam = {
            eventType: is_first_view ? 1 : 3,
            paramValue: this.data,
            touristId: getVistorId(),
        };

        flowAfter(param)
            .then((res) => {
                // 覆盖本地存储
                setLocalItem("flow", this.data);
            })
            .catch((error) => {
                console.error("页面浏览事件发送失败:", error);
            });
    }

    /**
     * 商品点击事件
     * @param id - 商品ID
     */
    ProductClick(id: string | number): void {
        if (!this.data) {
            return;
        }

        const param: FlowEventParam = {
            eventType: 5,
            paramValue: this.data,
            productsId: id,
            touristId: getVistorId(),
        };

        flowAfter(param).catch((error) => {
            console.error("商品点击事件发送失败:", error);
        });
    }

    /**
     * 注册事件
     * @param id - 用户ID
     */
    register(id: string | number): void {
        if (!this.data) {
            return;
        }

        const param: FlowEventParam = {
            eventType: 2,
            paramValue: this.data,
            customersId: id,
            touristId: getVistorId(),
        };

        flowAfter(param).catch((error) => {
            console.error("注册事件发送失败:", error);
        });
    }

    /**
     * 结算成功事件
     * @param res - 订单响应数据
     */
    checkSuccess(res: CheckSuccessResponse): void {
        if (!this.data) {
            return;
        }

        const param: FlowEventParam = {
            eventType: 4,
            paramValue: this.data,
            touristId: getVistorId(),
            ordersOid: res.extra.orders_oid,
            price: res.extra.order_total_out_shipping,
        };

        flowAfter(param).catch((error) => {
            console.error("结算成功事件发送失败:", error);
        });
    }
}

const addFlow = new Flow();

export default addFlow;
